import R from "ramda";
import moment from "moment";
import { fetchDocuments, putDocument, uploadDocuments } from "api/documents";
import { store } from "store";
import { setFlashMessageAction } from "actions/app-actions";
import { trackEventAction } from "actions/shared/tracking-actions";

export const DOCUMENT_UPLOADED = "actions/pending-documents/document-uploaded";
export const SET_PENDING_DOCUMENTS = "actions/pending-documents/set-pending-documents";
export const UPLOAD_ERROR = "actions/pending-documents/upload-error";
export const LOADING_START = "actions/pending-documents/loading-start";
export const FOCUS_PENDING_DOCUMENT = "actions/pending-documents/focus-pending-document";
export const CANCEL_PROCESSING = "actions/pending-documents/cancel-processing";
export const SET_TRASH_DOCUMENT_VISIBILITY = "actions/pending-documents/set-trash-document-visibility";
export const PROCESS_ALL_DOCUMENTS = "actions/pending-documents/process-all-documents";
export const CANCEL_PROCESSING_ALL_DOCUMENTS = "actions/pending-documents/cancel-processing-all-documents";
export const REPROCESS_PENDING_DOCUMENT = "actions/pending-documents/reprocess-pending-document";
export const CHANGE_DOCUMENT_TYPE = "actions/pending-documents/change-document-type";
export const UPLOAD_LOADING_START = "actions/pending-documents/upload-loading-start";
export const UPLOAD_LOADING_END = "actions/pending-documents/upload-loading-end";
export const CANCEL_PROCESSING_LOADING = "actions/pending-documents/cancel-processing-loading";
export const CANCEL_PROCESSING_LOADING_STOP = "actions/pending-documents/cancel-processing-loading-stop";
export const START_FILTERS_MODE = "actions/pending-documents/start-filters-mode";
export const END_FILTERS_MODE = "actions/pending-documents/end-filters-mode";
export const ADD_STUCK_STATUS_FILTER = "actions/pending-documents/add-stuck-status-filter";
export const REMOVE_STUCK_STATUS_FILTER = "actions/pending-documents/remove-stuck-status-filter";
export const ADD_SOURCE_FILTER = "actions/pending-documents/add-source-filter";
export const REMOVE_SOURCE_FILTER = "actions/pending-documents/remove-source-filter";

export const dropDocumentAction = (accountId, files) => (dispatch) => {
  dispatch({ type: UPLOAD_LOADING_START });
  const ajaxPromises = files.map(file => {
    const formData = new FormData();
    formData.append("document", JSON.stringify({ processingState: "NEEDS_SYSTEM_PROCESSING" }));
    formData.append("attachment", file);
    return uploadDocuments(accountId, formData)
      .then(document => {
        dispatch({
          type: DOCUMENT_UPLOADED,
          data: document
        });
        dispatch(trackEventAction("Document uploaded"));
      });
  });
  return Promise.all(ajaxPromises)
    .then(() => dispatch({ type: UPLOAD_LOADING_END }));
};

export const fetchPendingDocumentsAction = (accountId) => (dispatch) => {
  const pagination = {
    orderBy: "uploaded",
    orderDirection: "DSC",
    processingState: ["NEEDS_SYSTEM_PROCESSING", "NEEDS_USER_PROCESSING"]
  };
  const limit = { limit: 250, pageNumber: 1 };
  dispatch({
    type: LOADING_START
  });
  fetchDocuments(accountId, pagination, limit)
    .then(({ documents, totalCount, totalCountFiltered, totalSumFiltered }) => {
      const newDocuments = documents.map(document => {
        if (document.type === "unknown") {
          return R.set(R.lensProp("type"), "document", document);
        }
        return document;
      });
      dispatch({
        type: SET_PENDING_DOCUMENTS,
        data: {
          newDocuments,
          totalCount,
          totalCountFiltered,
          totalSumFiltered
        }
      });
    });
};

export const reloadPendingDocuments = (dispatch) => {
  setTimeout(() => {
    const state = store.getState();
    const account = state.app.selectedAccount;
    dispatch(fetchPendingDocumentsAction(account.id));
  }, 0);
};

export const uploadErrorAction = (error) => ({
  type: UPLOAD_ERROR,
  data: { error }
});

export const focusPendingDocumentAction = (document) => ({
  type: FOCUS_PENDING_DOCUMENT,
  data: { document }
});

export const cancelProcessingAction = (document) => (dispatch) => {
  const newDocument = R.clone(document);
  newDocument.processingState = "NEEDS_USER_PROCESSING";
  dispatch({
    type: CANCEL_PROCESSING,
    data: { newDocument }
  });
  dispatch(trackEventAction("Processing cancelled"));
  return putDocument(document.accountId, document.id, newDocument);
};

export const setTrashDocumentVisibilityAction = (visibility) => ({
  type: SET_TRASH_DOCUMENT_VISIBILITY,
  data: { visibility }
});

export const savePendingDocumentAction = () => (dispatch) => {
  const state = store.getState();
  const accountId = state.app.selectedAccount.id;
  const focusedDocument = state.pendingDocuments.focusedDocument;
  const formValues = R.path(["form", "pendingReceiptForm", "values"], state);
  const newCategories = formValues.categories.map(category => category.label || category);
  const newIssued = moment.isMoment(formValues.issued) ? formValues.issued.utc().format() : moment().utc().format();
  const newDocument = R.merge(focusedDocument, {
    categories: newCategories,
    currency: formValues.currency.key,
    invoiceNumber: formValues.invoiceNumber,
    issued: newIssued,
    notes: formValues.notes,
    paymentType: { type: formValues.formPaymentType.key, lastFourDigits: formValues.cardNumber },
    processingState: "PROCESSED",
    tax: formValues.tax,
    total: formValues.total,
    type: focusedDocument.type === "unknown" ? "receipt" : focusedDocument.type,
    vendor: formValues.vendor
  });
  dispatch(trackEventAction("Pending document saved"));
  putDocument(accountId, focusedDocument.id, newDocument)
    .then(() => {
      reloadPendingDocuments(dispatch);
    });
};

export const savePendingOtherDocumentAction = () => (dispatch) => {
  const state = store.getState();
  const accountId = state.app.selectedAccount.id;
  const focusedDocument = state.pendingDocuments.focusedDocument;
  const formValues = R.path(["form", "pendingOtherDocumentForm", "values"], state);
  const newIssued = formValues.issued ? formValues.issued.utc().format() : moment().utc().format();
  const newDocument = {
    id: focusedDocument.id,
    issued: newIssued,
    name: formValues.name,
    notes: formValues.notes,
    processingState: "PROCESSED",
    type: "other"
  };
  dispatch(trackEventAction("Pending other document saved"));
  putDocument(accountId, focusedDocument.id, newDocument)
    .then(() => {
      reloadPendingDocuments(dispatch);
    });
};

export const trashPendingDocumentAction = (accountId, document) => (dispatch) => (
  putDocument(accountId, document.id, R.assoc("trashed", true, document))
    .then(() => {
      reloadPendingDocuments(dispatch);
      dispatch(trackEventAction("Pending document trashed"));
    })
);

export const processAllDocumentsAction = (pendingDocuments) => (dispatch) => {
  const ajaxPromises = pendingDocuments.map(document => {
    const newDocument = R.clone(document);
    newDocument.processingState = "NEEDS_SYSTEM_PROCESSING";
    return putDocument(document.accountId, document.id, newDocument);
  });
  return Promise.all(ajaxPromises)
    .then((response) => {
      const documents = R.map((n) => n.data, response);
      dispatch({
        type: PROCESS_ALL_DOCUMENTS,
        data: { documents }
      });
      dispatch(trackEventAction("Process all documents"));
      reloadPendingDocuments(dispatch);
    });
};

export const cancelProcessingAllDocumentsAction = (pendingDocuments) => (dispatch) => {
  dispatch({ type: CANCEL_PROCESSING_LOADING });
  const ajaxPromises = pendingDocuments.map(document => {
    const newDocument = R.clone(document);
    newDocument.processingState = "NEEDS_USER_PROCESSING";
    return putDocument(document.accountId, document.id, newDocument);
  });
  return Promise.all(ajaxPromises)
    .then((response) => {
      const documents = R.map((n) => n.data, response);
      dispatch({
        type: CANCEL_PROCESSING_ALL_DOCUMENTS,
        data: { documents }
      });
      dispatch({ type: CANCEL_PROCESSING_LOADING_STOP });
      dispatch(trackEventAction("Cancel processing all documents"));
    });
};

export const reprocessPendingDocumentAction = (document) => (dispatch) => {
  const newDocument = R.clone(document);
  newDocument.processingState = "NEEDS_SYSTEM_PROCESSING";
  dispatch({
    type: REPROCESS_PENDING_DOCUMENT,
    data: { newDocument }
  });
  return putDocument(document.accountId, document.id, newDocument)
    .then(() => {
      reloadPendingDocuments(dispatch);
    });
};

export const changeDocumentTypeAction = (type) => ({
  type: CHANGE_DOCUMENT_TYPE,
  data: { type }
});

export const dropRejectAction = () => (dispatch) => (
  dispatch(setFlashMessageAction(`Upload Failed. Please check that the document is under 8MB and that
  it is one of the following file types: .jpg, .jpeg, .gif, .png, .tiff, .pdf, .txt, .doc, .docx,
  .csv, .xls, .xlsx, .ods, .odt`, "error"))
);

export const filterPendingDocumentsAction = (filterType) => (dispatch) => {
  const state = store.getState();
  const pendingDocuments = state.pendingDocuments.pendingDocuments;
  const filter = state.pendingDocuments[filterType];
  const newPendingDocuments = pendingDocuments.filter((document) => {
    if (
        (filter.key === "status_stuck"
        && document.processingState === "NEEDS_OVERALL_PROCESSING"
        && document.queued_for_data_entry === false)
        ||
        (filter.key === "status_not_stuck" && document.processingState !== "NEEDS_OVERALL_PROCESSING")
        ||
        (filter.key === "source_email" && document.source.type === "email")
        ||
        (filter.key === "source_mobile" && document.source.type === "mobile")
        ||
        (filter.key === "source_integration" && document.source.type === "integration")
      ) {
      return document;
    }
    return null;
  });
  return dispatch({
    type: SET_PENDING_DOCUMENTS,
    data: { newDocuments: newPendingDocuments }
  });
};

export const startFiltersModeAction = () => ({
  type: START_FILTERS_MODE
});

export const endFiltersModeAction = () => ({
  type: END_FILTERS_MODE
});

export const addStuckStatusFilterAction = (filter) => (dispatch) => {
  dispatch({
    type: ADD_STUCK_STATUS_FILTER,
    data: { filter }
  });
  return dispatch(filterPendingDocumentsAction("statusFilter"));
};

export const removeStuckStatusFilterAction = (accountId) => (dispatch) => {
  dispatch(fetchPendingDocumentsAction(accountId));
  return dispatch({ type: REMOVE_STUCK_STATUS_FILTER });
};

export const addSourceFilterAction = (filter) => (dispatch) => {
  dispatch({
    type: ADD_SOURCE_FILTER,
    data: { filter }
  });
  return dispatch(filterPendingDocumentsAction("sourceFilter"));
};

export const removeSourceFilterAction = (accountId) => (dispatch) => {
  dispatch(fetchPendingDocumentsAction(accountId));
  return dispatch({ type: REMOVE_SOURCE_FILTER });
};




// WEBPACK FOOTER //
// ./src/js/actions/pending-documents-actions.js